import { Badge, Card, CardContent, CardHeader, ErrorBanner, Spinner } from '@/components/ui';
import { portfolioApi } from '@/services/portfolio-api';
import type { IProductP85Response, TrendDirection } from '@skystone/shared';
import type { IMetricViewFilters } from '@/hooks/use-metric-view-filters';
import { useCardFetch } from '@/hooks/use-card-fetch';
import { useCardWidth } from '@/hooks/use-card-width';
import { CycleTimeP85Stacked } from './CycleTimeP85Stacked';
import { CARD_WIDTH_THRESHOLDS } from '@/lib/metric-view/responsive-thresholds';
import { cn } from '@/lib/utils';

interface ICycleTimeP85CardProps {
  filters: IMetricViewFilters;
  /**
   * Selected team's display name, resolved by the page from the scope
   * options. When set, rows are labeled with this value instead of the
   * backend's product_name (the endpoint groups by product regardless of
   * team filter, so without this override the "Team" column would show a
   * product name — confusing when a specific team is active in scope).
   */
  selectedTeamName?: string | null;
}

const DELTA_ARROWS: Record<'up' | 'down' | 'flat', string> = {
  up: '▲',
  down: '▼',
  flat: '■',
};

const DELTA_STYLES: Record<'up' | 'down' | 'flat', string> = {
  up: 'text-error',
  down: 'text-success',
  flat: 'text-muted-foreground',
};

function TypeCell({
  p85,
  deltaPct,
  direction,
}: Readonly<{ p85: number | null; deltaPct: number | null; direction: TrendDirection | null }>) {
  if (p85 === null) {
    return <span className="text-muted-foreground">—</span>;
  }
  return (
    <span className="flex items-center justify-end gap-1.5 tabular-nums">
      <span className="font-semibold text-foreground">
        {Math.round(p85)}
        <span className="ml-0.5 text-[10px] font-normal text-muted-foreground">d</span>
      </span>
      {direction !== null && deltaPct !== null && (
        <span className={cn('text-[10px] font-medium shrink-0', DELTA_STYLES[direction])}>
          {DELTA_ARROWS[direction]} {Math.abs(Math.round(deltaPct))}%
        </span>
      )}
    </span>
  );
}

function resolveGroupLabel(filters: IMetricViewFilters): 'PRODUCT' | 'TEAM' {
  // v1 deviation: backend has getCycleTimeP85ByProductAndType only. When scope
  // narrows to a single product/team, the same product-level endpoint is used
  // with a WHERE filter. Spec's "group by team when product selected" requires
  // a new backend endpoint (filed as follow-up bd issue).
  return filters.productId || filters.teamId ? 'TEAM' : 'PRODUCT';
}

const HEADER_CELL = 'px-2 py-1.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground';
const BODY_CELL = 'px-2 py-1.5 text-[11px]';

export function CycleTimeP85Card({ filters, selectedTeamName = null }: Readonly<ICycleTimeP85CardProps>) {
  const { ref, width } = useCardWidth<HTMLDivElement>();
  const { data, loading, error } = useCardFetch<IProductP85Response>(
    () => portfolioApi.getCycleTimeP85ByProduct({
      from: filters.from,
      to: filters.to,
      productId: filters.productId || undefined,
      teamId: filters.teamId || undefined,
      includeArchived: filters.includeArchived || undefined,
    }),
    [filters.from, filters.to, filters.productId, filters.teamId, filters.includeArchived],
    'Failed to load cycle time P85.',
  );

  const groupLabel = resolveGroupLabel(filters);
  const rows = data?.products ?? [];
  const useStacked = width > 0 && width < CARD_WIDTH_THRESHOLDS.p85.stacked;
  const useScrollTable = width > 0 && !useStacked && width < CARD_WIDTH_THRESHOLDS.p85.scrollTable;

  return (
    <Card ref={ref}>
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Cycle Time P85</h3>
            <p className="text-[11px] text-muted-foreground">
              85th-percentile cycle time per issue type, with change vs previous period.
            </p>
          </div>
          {data && (
            <Badge variant="secondary" className="shrink-0 text-[10px] uppercase">
              Grouped by: {groupLabel}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading && <div className="flex h-40 items-center justify-center"><Spinner /></div>}
        {!loading && error && <ErrorBanner message={error} />}
        {!loading && !error && data && rows.length === 0 && (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No products match the current filters.
          </p>
        )}
        {!loading && !error && rows.length > 0 && useStacked && (
          <CycleTimeP85Stacked rows={rows} groupLabel={groupLabel} selectedTeamName={selectedTeamName} />
        )}
        {!loading && !error && rows.length > 0 && !useStacked && (
          <div className="overflow-x-auto">
            <table className={useScrollTable ? 'min-w-[360px] text-[11px]' : 'w-full text-[11px]'}>
              <thead>
                <tr>
                  <th scope="col" className={cn(HEADER_CELL, 'text-left')}>
                    {groupLabel === 'PRODUCT' ? 'Product' : 'Team'}
                  </th>
                  <th scope="col" className={cn(HEADER_CELL, 'text-right')}>All</th>
                  <th scope="col" className={cn(HEADER_CELL, 'text-right')}>Story</th>
                  <th scope="col" className={cn(HEADER_CELL, 'text-right')}>Bug</th>
                  <th scope="col" className={cn(HEADER_CELL, 'text-right')}>Task</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.productName} className="border-t border-border">
                    <td className={cn(BODY_CELL, 'text-left font-medium text-foreground truncate')}>
                      {selectedTeamName ?? row.productName}
                    </td>
                    <td className={cn(BODY_CELL, 'text-right')}>
                      <TypeCell p85={row.overallP85} deltaPct={row.overallDeltaPct} direction={row.overallTrendDirection} />
                    </td>
                    <td className={cn(BODY_CELL, 'text-right')}>
                      <TypeCell p85={row.storyP85} deltaPct={row.storyDeltaPct} direction={row.storyTrendDirection} />
                    </td>
                    <td className={cn(BODY_CELL, 'text-right')}>
                      <TypeCell p85={row.bugP85} deltaPct={row.bugDeltaPct} direction={row.bugTrendDirection} />
                    </td>
                    <td className={cn(BODY_CELL, 'text-right')}>
                      <TypeCell p85={row.taskP85} deltaPct={row.taskDeltaPct} direction={row.taskTrendDirection} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
